import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import {
  FileText,
  Info,
  Check,
  Star,
  Sparkles,

  Search,
  CheckCircle,
  Plus,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../foundation/altzero_base/base/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import { useDocumentForm } from "../contexts/DocumentFormContext";
import { StepProps, TemplateSection } from "../types/document-form";
import { templateStorageService, type Template } from "../services/templateStorageService";
// useToast removed for cleaner workflow experience
import { motion } from "framer-motion";
import { WorkflowNavigation } from "./WorkflowNavigation";

interface TemplateSelectionStepProps extends StepProps {
  preSelectedTemplate?: any;
}

const TemplateSelectionStep: React.FC<TemplateSelectionStepProps> = ({
  onNext,
  onBack,
  preSelectedTemplate,
}) => {
  const {
    selectedTemplate,
    referenceDocument,
    handleTemplateSelect,
    isCurrentStepValid,
    user, // Get user from context
  } = useDocumentForm();

  // Toast removed for cleaner workflow experience
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateSection[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"templates" | "documents">(
    "templates" // Always start with templates tab
  );
  const [showAllTemplatesModal, setShowAllTemplatesModal] = useState(false);
  const [showAllDocumentsModal, setShowAllDocumentsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [documentsSearchQuery, setDocumentsSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [documentsCurrentPage, setDocumentsCurrentPage] = useState(1);
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] =
    useState<TemplateSection | null>(null);
  const [selectedDocumentForPreview, setSelectedDocumentForPreview] =
    useState<any>(null);

  const TEMPLATES_PER_PAGE = 12;

  // Determine if this is an AI Generation flow (no preSelectedTemplate and no existing document flow)
  const isAiGenerationFlow = !preSelectedTemplate ||
    (preSelectedTemplate.type !== "existing_document" && preSelectedTemplate.type !== "existing_proposal");

  console.log("🎯 TemplateSelectionStep - Flow Detection:", {
    isAiGenerationFlow,
    preSelectedTemplate,
    willLoadExistingDocuments: !isAiGenerationFlow
  });

  // Helper function to get visible templates (first 3 or include selected)
  const getVisibleTemplates = () => {
    console.log("🔍 getVisibleTemplates called:", {
      selectedTemplate,
      totalTemplates: templates.length,
      preSelectedTemplate: preSelectedTemplate?.type,
    });

    if (
      preSelectedTemplate &&
      (preSelectedTemplate.type === "existing_document" || preSelectedTemplate.type === "existing_proposal")
    ) {
      return [createTemplateFromPreSelected(preSelectedTemplate)];
    }

    // Get first 3 templates from the full templates array
    const first3Templates = templates.slice(0, 3);

    // If no template is selected, show first 3
    if (!selectedTemplate) {
      console.log(
        "📋 No template selected, showing first 3:",
        first3Templates.map((t) => t.id)
      );
      return first3Templates;
    }

    // Find the selected template in the FULL templates array (not just initialTemplates)
    const selectedTemplateObj = templates.find(
      (t) => t.id === selectedTemplate
    );
    if (!selectedTemplateObj) {
      console.log(
        "❌ Selected template not found in templates array:",
        selectedTemplate
      );
      return first3Templates;
    }

    // If selected template is in first 3, show first 3
    if (first3Templates.some((t) => t.id === selectedTemplate)) {
      console.log(
        "✅ Selected template is in first 3, showing first 3:",
        first3Templates.map((t) => t.id)
      );
      return first3Templates;
    }

    // If selected template is not in first 3, replace the last one with selected
    const visibleTemplates = [
      ...first3Templates.slice(0, 2),
      selectedTemplateObj,
    ];
    console.log(
      "🔄 Selected template not in first 3, showing:",
      visibleTemplates.map((t) => t.id)
    );
    return visibleTemplates;
  };

  // Helper function to get visible documents (first 6 or include selected)
  const getVisibleDocuments = () => {
    // If no document is selected, show first 6
    if (!selectedTemplate || !selectedTemplate.startsWith("doc_")) {
      return existingDocuments.slice(0, 6);
    }

    // Extract document ID from selectedTemplate (format: "doc_123")
    const selectedDocId = selectedTemplate.replace("doc_", "");
    const selectedDocumentObj = existingDocuments.find(
      (d) => d.id.toString() === selectedDocId
    );

    if (!selectedDocumentObj) {
      return existingDocuments.slice(0, 6);
    }

    // If selected document is in first 6, show first 6
    const first6 = existingDocuments.slice(0, 6);
    if (first6.some((d) => d.id.toString() === selectedDocId)) {
      return first6;
    }

    // If selected document is not in first 6, replace the last one with selected
    const visibleDocuments = [...first6.slice(0, 5), selectedDocumentObj];
    return visibleDocuments;
  };



  // Handle tab change and refresh data
  const handleTabChange = (tab: "templates" | "documents") => {
    setActiveTab(tab);
    if (tab === "documents" && !isAiGenerationFlow) {
      // Refresh existing documents when switching to documents tab (only if not AI Generation flow)
      loadExistingDocuments();
    }
  };

  // Handle template selection from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const templateId = urlParams.get("template");

    if (templateId) {
      console.log("🔍 Template ID from URL:", templateId);

      // If templates are loaded, select immediately
      if (templates.length > 0) {
        const templateExists = templates.some(
          (template) => template.id === templateId
        );
        if (templateExists) {
          console.log("✅ Selecting template:", templateId);
          handleTemplateSelect(templateId);
        } else {
          console.error("❌ Template not found:", templateId);
          console.log(
            "Available templates:",
            templates.map((t) => t.id)
          );
        }
      }

      // Always clear the URL parameter immediately
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
      console.log("🧹 Cleared URL parameter, new URL:", newUrl);
    }
  }, [templates, handleTemplateSelect]); // Run when templates change

  // Check URL parameter on mount (before templates load)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const templateId = urlParams.get("template");

    if (templateId) {
      console.log("🚀 Component mounted with template ID:", templateId);
      // Store the template ID to select after templates load
      sessionStorage.setItem("pendingTemplateSelection", templateId);

      // Clear URL immediately
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
      console.log("🧹 URL cleared on mount");
    }
  }, []);

  // Handle pre-selected template from props
  useEffect(() => {
    if (preSelectedTemplate) {
      console.log("🎯 Pre-selected template detected:", preSelectedTemplate);

      // Auto-select the pre-selected template
      if (preSelectedTemplate.id) {
        handleTemplateSelect(preSelectedTemplate.id);
        console.log(
          "✅ Auto-selected pre-selected template:",
          preSelectedTemplate.id
        );
      }
    }
  }, [preSelectedTemplate, handleTemplateSelect]);

  // Create template object from pre-selected existing document or proposal
  const createTemplateFromPreSelected = (preSelected: any): TemplateSection => {
    const isProposal = preSelected.type === "existing_proposal";

    return {
      id: preSelected.id,
      name: preSelected.title,
      description: isProposal
        ? `Using existing proposal as template - Originally created for ${preSelected.original_client}`
        : `Generated document template - ${preSelected.source || "Existing Document"}`,
      sections: isProposal
        ? ["Executive Summary", "Project Overview", "Scope of Work", "Timeline", "Investment", "Terms & Conditions", "Next Steps"]
        : ["Introduction", "Main Content", "Conclusion"],
      isDefault: false,
      category: isProposal ? "Existing Proposal" : "Generated Document",
    };
  };

  // Load templates and existing documents from database
  useEffect(() => {
    // Only load templates if user is available and no pre-selected existing document
    if (user?.id) {
      if (
        !preSelectedTemplate ||
        (preSelectedTemplate.type !== "existing_document" && preSelectedTemplate.type !== "existing_proposal")
      ) {
        loadTemplates();
      } else {
        // If we have a pre-selected existing document or proposal, just set loading to false
        setIsLoading(false);
      }

      // Only load existing documents if NOT in AI Generation flow
      if (!isAiGenerationFlow) {
        loadExistingDocuments();
      } else {
        console.log("🚀 AI Generation flow detected - skipping existing documents loading");
      }
    }
  }, [preSelectedTemplate, user?.id, isAiGenerationFlow]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);

      // Only load templates if user is available
      if (!user?.id) {
        console.log("⏳ Waiting for user to load templates...");
        setIsLoading(false);
        return;
      }

      console.log("🔄 Loading templates for user:", user.id);
      const result = await templateStorageService.getUserTemplates(user.id);

      if (!result.success) {
        console.error("❌ Error loading templates:", result.error);
        setTemplates([]);
        return;
      }

      // Convert Template to TemplateSection format
      const convertedTemplates: TemplateSection[] = (result.templates || []).map(
        (template: Template) => ({
          id: template.id,
          name: template.name,
          description: template.description || "Professional template",
          content: "", // Will be loaded when needed
          sections: ["Introduction", "Main Content", "Conclusion"], // Default sections for now
          isDefault: false,
          category: "Template",
        })
      );

      setTemplates(convertedTemplates);
      console.log(
        "📚 Loaded templates:",
        convertedTemplates.map((t) => ({ id: t.id, name: t.name }))
      );

      // Check for pending template selection
      const pendingTemplateId = sessionStorage.getItem(
        "pendingTemplateSelection"
      );
      if (pendingTemplateId) {
        console.log("🎯 Found pending template selection:", pendingTemplateId);
        const templateExists = convertedTemplates.some(
          (template) => template.id === pendingTemplateId
        );

        if (templateExists) {
          console.log("✅ Selecting pending template:", pendingTemplateId);
          handleTemplateSelect(pendingTemplateId);
          sessionStorage.removeItem("pendingTemplateSelection");

          // Template selected successfully - no toast needed during workflow
        } else {
          console.error("❌ Pending template not found:", pendingTemplateId);
          console.log(
            "Available template IDs:",
            convertedTemplates.map((t) => t.id)
          );
          sessionStorage.removeItem("pendingTemplateSelection");
        }
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      // Error logged to console - no toast needed during workflow
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingDocuments = async () => {
    try {
      setIsLoading(true);
      console.log(
        "📄 Loading existing documents from scopingai_documents table..."
      );

      if (!user?.id) {
        console.warn("❌ No user ID available for loading documents");
        setExistingDocuments([]);
        return;
      }

      // Import supabase client
      const { supabase } = await import("../../../foundation/altzero_base/base/utils/supabaseClient");

      // Fetch documents from scopingai_documents table (same as DocumentsLibrary)
      const { data, error } = await supabase
        .from("scopingai_documents")
        .select(
          `
          id,
          title,
          created_at,
          updated_at,
          status,
          content,
          metadata,
          client_id,
          crm_companies (
            name
          )
        `
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error loading existing documents:", error);
        throw error;
      }

      // Transform the data to match the expected format (same as DocumentsLibrary)
      const transformedDocuments = (data || []).map((doc: any) => ({
        id: doc.id,
        title: doc.title,
        client: doc.crm_companies?.name || "No Client",
        type: "Generated Document",
        date: new Date(doc.created_at).toLocaleDateString(),
        status: doc.status || "completed",
        content: doc.content,
        metadata: doc.metadata,
        markdown_content: doc.content?.markdown || "",
        sections: doc.content?.sections || [],
      }));

      console.log("📄 Loaded and transformed documents:", transformedDocuments);
      setExistingDocuments(transformedDocuments);
    } catch (error) {
      console.error("❌ Failed to load existing documents:", error);
      // Error logged to console - no toast needed during workflow
      setExistingDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter templates based on search query
  const filteredTemplates = React.useMemo(() => {
    if (!searchQuery.trim()) return templates;

    const query = searchQuery.toLowerCase().trim();
    return templates.filter(
      (template) =>
        (template.name && template.name.toLowerCase().includes(query)) ||
        (template.description &&
          template.description.toLowerCase().includes(query)) ||
        (template.sections &&
          template.sections.some((section: any) =>
            typeof section === "string"
              ? section.toLowerCase().includes(query)
              : section.name && section.name.toLowerCase().includes(query)
          ))
    );
  }, [templates, searchQuery]);

  // Get paginated templates
  const paginatedTemplates = React.useMemo(() => {
    const startIndex = (currentPage - 1) * TEMPLATES_PER_PAGE;
    return filteredTemplates.slice(startIndex, startIndex + TEMPLATES_PER_PAGE);
  }, [filteredTemplates, currentPage]);

  // Note: We now use getVisibleTemplates() instead of initialTemplates
  // to ensure selected templates are always visible

  // Handle template preview
  const handleTemplatePreview = (template: TemplateSection) => {
    setSelectedTemplateForPreview(template);
  };

  // Handle template search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle pagination
  const totalPages = Math.ceil(filteredTemplates.length / TEMPLATES_PER_PAGE);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Update the description of the custom template if a reference document is selected
  if (referenceDocument) {
    const customTemplate = templates.find((t) => t.id === "custom");
    if (customTemplate) {
      customTemplate.description = "Using structure from reference document";
      customTemplate.sections = referenceDocument.sections;
    }
  }

  return (
    <div className="flex flex-col ">
      {/* Main content area with bottom padding for navigation */}
      <div className="flex-1 space-y-4 pb-4 ">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              Select the Scoping Structure{" "}
              <span className="text-red-500">*</span>
            </h2>
            <p className="text-muted-foreground">
              Choose a structure that matches your project
              requirements(required)
            </p>
            {!selectedTemplate && (
              <p className="text-sm text-red-500 mt-1">
                Please select a scoping structure to continue
              </p>
            )}
          </div>

          {/* See More Templates Button */}
          {/* <Button
            variant="outline"
            onClick={() => setShowAllTemplatesModal(true)}
            className="gap-2 bg-primary/5 border-primary/20 text-primary hover:bg-primary/10"
          >
            <Search className="w-4 h-4" />
            See More Templates
          </Button> */}
        </div>

        {referenceDocument && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
            <div className="flex items-start">
              <Info size={18} className="text-amber-500 mr-2 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Template selection is set to "Custom" based on your reference
                  document
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  The structure and format will be based on "
                  {referenceDocument.title}". Remove the reference document if
                  you want to select a different template.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs for Templates and Existing Documents - Hide when pre-selected existing document */}
        {!(
          preSelectedTemplate &&
          preSelectedTemplate.type === "existing_document"
        ) && (
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 justify-between items-center">
              <div className="flex space-x-8">
                <button
                  onClick={() => handleTabChange("templates")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "templates"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Templates ({templates.length})
                </button>
                {/* Only show Existing Documents tab if NOT in AI Generation flow */}
                {!isAiGenerationFlow && (
                  <button
                    onClick={() => handleTabChange("documents")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "documents"
                        ? "border-primary text-primary"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Existing Documents ({existingDocuments.length})
                  </button>
                )}
              </div>

              {/* Only show the Templates tab */}
              {/* <div className="flex space-x-8">
                <button
                  onClick={() => handleTabChange("templates")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "templates"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Templates ({templates.length})
                </button>
              </div> */}
              <div className="flex items-center gap-3">
                {/* Create Template Button - Always visible when on Templates tab */}
                {activeTab === "templates" && (
                  <Button
                    onClick={() => {
                      sessionStorage.setItem("scopingai_returnToStep", "2");
                      sessionStorage.setItem("scopingai_createTemplateMode", "true");
                      window.open("/tender/template-editor", "_blank");
                    }}
                    size="sm"
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Create Template
                  </Button>
                )}

                {/* Refresh button for documents tab */}
                {activeTab === "documents" && !isAiGenerationFlow && (
                  <button
                    onClick={loadExistingDocuments}
                    className="py-1 px-3 text-xs bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    🔄 Refresh
                  </button>
                )}
              </div>
            </nav>
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="pb-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="flex gap-1">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                      <div className="h-6 bg-gray-200 rounded w-14"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="mt-6">
            {/* Show header when pre-selected existing document */}
            {preSelectedTemplate &&
              preSelectedTemplate.type === "existing_document" && (
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText
                      className="text-blue-600 dark:text-blue-400"
                      size={20}
                    />
                    <div>
                      <h3 className="font-semibold text-blue-800 dark:text-blue-200">
                        Using Selected Document as Template
                      </h3>
                      <p className="text-blue-700 dark:text-blue-300 text-sm">
                        "{preSelectedTemplate.title}" will be used as the
                        template structure for your new document.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {activeTab === "templates" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {/* Show visible templates (first 3 or include selected) */}
                {getVisibleTemplates().map((template) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card
                      key={template.id}
                      className={`transition-all ${
                        // Disable if reference document exists and not custom template
                        (referenceDocument && template.id !== "custom") ||
                        // Disable if pre-selected template exists and this is not the selected one
                        (preSelectedTemplate &&
                          template.id !== preSelectedTemplate.id)
                          ? "opacity-50 cursor-not-allowed"
                          : selectedTemplate === template.id
                          ? "border-primary ring-2 ring-primary/20 cursor-pointer"
                          : "hover:border-primary/50 cursor-pointer"
                      }`}
                      onClick={() => {
                        // Only allow selection if not disabled
                        if (
                          !(referenceDocument && template.id !== "custom") &&
                          !(
                            preSelectedTemplate &&
                            template.id !== preSelectedTemplate.id
                          )
                        ) {
                          handleTemplateSelect(template.id);
                        }
                      }}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0 pr-2">
                            <CardTitle className="truncate">
                              {template.name}
                            </CardTitle>
                            <CardDescription className="line-clamp-2">
                              {template.description}
                            </CardDescription>
                          </div>
                          {selectedTemplate === template.id && (
                            <div className="bg-primary text-primary-foreground rounded-full p-1 flex-shrink-0">
                              <Check size={16} />
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {preSelectedTemplate &&
                              template.id === preSelectedTemplate.id && (
                                <Badge
                                  variant="default"
                                  className="text-xs bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Pre-selected
                                </Badge>
                              )}
                            {template.isDefault && (
                              <Badge variant="secondary" className="text-xs">
                                <Star className="w-3 h-3 mr-1" />
                                Default
                              </Badge>
                            )}
                            {template.category && (
                              <Badge variant="outline" className="text-xs">
                                {template.category}
                              </Badge>
                            )}
                          </div>

                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {existingDocuments.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium mb-2">
                      No existing documents
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Upload your existing proposal documents to use as
                      templates
                    </p>
                    <Link to="/tender/documents/library">
                      <Button variant="outline">Upload Documents</Button>
                    </Link>
                  </div>
                ) : (
                  getVisibleDocuments().map((document) => (
                    <motion.div
                      key={document.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card
                        className={`transition-all cursor-pointer ${
                          selectedTemplate === `doc_${document.id}`
                            ? "border-primary ring-2 ring-primary/20"
                            : "hover:border-primary/50"
                        }`}
                        onClick={() => {
                          console.log("🎯 Existing document selected:", {
                            documentId: document.id,
                            templateId: `doc_${document.id}`,
                            documentTitle: document.title,
                          });
                          handleTemplateSelect(`doc_${document.id}`);
                        }}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0 pr-2">
                              <CardTitle className="text-base font-semibold mb-1 truncate">
                                {document.title}
                              </CardTitle>
                              <CardDescription className="text-sm text-muted-foreground truncate">
                                Client: {document.client}
                              </CardDescription>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                <span className="truncate">
                                  {document.date}
                                </span>
                                <span>•</span>
                                <span className="capitalize truncate">
                                  {document.status}
                                </span>
                              </div>
                            </div>
                            {selectedTemplate === `doc_${document.id}` && (
                              <div className="bg-primary text-primary-foreground rounded-full p-1 flex-shrink-0">
                                <Check size={16} />
                              </div>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              <FileText className="w-3 h-3 mr-1" />
                              {document.type}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              Generated Document
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* See More Documents Button - Only show if NOT in AI Generation flow */}
            {activeTab === "documents" && existingDocuments.length > 0 && !isAiGenerationFlow && (
              <div className="flex justify-center mt-6 mb-24">
                <Button
                  variant="outline"
                  onClick={() => setShowAllDocumentsModal(true)}
                  className="gap-2 bg-primary/10 border-primary/30 text-primary font-semibold text-lg px-8 py-4 shadow-md hover:bg-primary/20 transition-all"
                  style={{ minWidth: 220 }}
                >
                  <Search className="w-5 h-5" />
                  See More Documents
                </Button>
              </div>
            )}
          </div>
        )}

        {/* See More Templates Modal */}
        <Dialog
          open={showAllTemplatesModal}
          onOpenChange={setShowAllTemplatesModal}
        >
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                All Templates
              </DialogTitle>
              <DialogDescription>
                Browse and search through all available templates
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Create Template Button */}
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search templates by name, description, or content..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
                <Button
                  onClick={() => {
                    sessionStorage.setItem("scopingai_returnToStep", "2");
                    sessionStorage.setItem(
                      "scopingai_createTemplateMode",
                      "true"
                    );
                    window.open("/tender/templates/create", "_blank");
                  }}
                  className="ml-4 gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Template
                </Button>
              </div>

              {/* Templates Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedTemplates.map((template) => (
                  <Card
                    key={template.id}
                    className={`transition-all cursor-pointer ${
                      selectedTemplate === template.id
                        ? "border-primary ring-2 ring-primary/20"
                        : "hover:border-primary/50"
                    }`}
                    onClick={() => {
                      handleTemplateSelect(template.id);
                      setShowAllTemplatesModal(false);
                    }}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-sm font-medium line-clamp-2">
                          {template.name}
                        </CardTitle>
                        {selectedTemplate === template.id && (
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
                        )}
                      </div>
                      <CardDescription className="text-xs line-clamp-2">
                        {template.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs">
                          {template.sections?.length || 0} sections
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTemplatePreview(template);
                          }}
                          className="text-xs h-6 px-2"
                        >
                          Preview
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * TEMPLATES_PER_PAGE + 1} to{" "}
                    {Math.min(
                      currentPage * TEMPLATES_PER_PAGE,
                      filteredTemplates.length
                    )}{" "}
                    of {filteredTemplates.length} templates
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>



        {/* See More Documents Modal - Only show if NOT in AI Generation flow */}
        {!isAiGenerationFlow && (
          <Dialog
            open={showAllDocumentsModal}
            onOpenChange={setShowAllDocumentsModal}
          >
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                All Existing Documents ({existingDocuments.length})
              </DialogTitle>
              <DialogDescription>
                Browse and select from all your existing proposal documents
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4">
              {/* Search Bar */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search documents by title, client, or content..."
                  value={documentsSearchQuery}
                  onChange={(e) => setDocumentsSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Documents Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {existingDocuments
                  .filter((document) =>
                    documentsSearchQuery === ""
                      ? true
                      : document.title
                          .toLowerCase()
                          .includes(documentsSearchQuery.toLowerCase()) ||
                        document.client
                          .toLowerCase()
                          .includes(documentsSearchQuery.toLowerCase())
                  )
                  .slice(
                    (documentsCurrentPage - 1) * TEMPLATES_PER_PAGE,
                    documentsCurrentPage * TEMPLATES_PER_PAGE
                  )
                  .map((document) => (
                    <Card
                      key={document.id}
                      className={`transition-all cursor-pointer ${
                        selectedTemplate === `doc_${document.id}`
                          ? "border-primary ring-2 ring-primary/20"
                          : "hover:border-primary/50"
                      }`}
                      onClick={() => {
                        handleTemplateSelect(`doc_${document.id}`);
                        setShowAllDocumentsModal(false);
                      }}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-sm font-medium line-clamp-2">
                            {document.title}
                          </CardTitle>
                          {selectedTemplate === `doc_${document.id}` && (
                            <div className="bg-primary text-primary-foreground rounded-full p-1 flex-shrink-0 ml-2">
                              <Check size={12} />
                            </div>
                          )}
                        </div>
                        <CardDescription className="text-xs line-clamp-1">
                          Client: {document.client}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              <FileText className="w-3 h-3 mr-1" />
                              {document.type}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {document.date}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            Generated Document
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDocumentForPreview(document);
                            }}
                            className="text-xs h-6 px-2"
                          >
                            Preview
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>

              {/* Pagination for Documents */}
              {(() => {
                const filteredDocuments = existingDocuments.filter((document) =>
                  documentsSearchQuery === ""
                    ? true
                    : document.title
                        .toLowerCase()
                        .includes(documentsSearchQuery.toLowerCase()) ||
                      document.client
                        .toLowerCase()
                        .includes(documentsSearchQuery.toLowerCase())
                );
                const totalPages = Math.ceil(
                  filteredDocuments.length / TEMPLATES_PER_PAGE
                );

                return totalPages > 1 ? (
                  <div className="flex items-center justify-between mt-6">
                    <p className="text-sm text-gray-600">
                      Showing{" "}
                      {(documentsCurrentPage - 1) * TEMPLATES_PER_PAGE + 1} to{" "}
                      {Math.min(
                        documentsCurrentPage * TEMPLATES_PER_PAGE,
                        filteredDocuments.length
                      )}{" "}
                      of {filteredDocuments.length} documents
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setDocumentsCurrentPage((prev) =>
                            Math.max(1, prev - 1)
                          )
                        }
                        disabled={documentsCurrentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="flex items-center px-3 text-sm">
                        Page {documentsCurrentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setDocumentsCurrentPage((prev) =>
                            Math.min(totalPages, prev + 1)
                          )
                        }
                        disabled={documentsCurrentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* 2. Show "See More Templates" button only when on Templates tab */}
      {activeTab === "templates" && (
        <div className="flex justify-center mt-2 mb-24">
          <Button
            variant="outline"
            onClick={() => {
              // Open the modal instead of navigating to a new tab
              setShowAllTemplatesModal(true);
            }}
            className="gap-2 bg-primary/10 border-primary/30 text-primary font-semibold text-lg px-8 py-4 shadow-md hover:bg-primary/20 transition-all"
            style={{ minWidth: 220 }}
          >
            <Search className="w-5 h-5" />
            See More Templates
          </Button>
        </div>
      )}

      {/* Navigation */}
      <WorkflowNavigation
        onBack={onBack}
        onNext={onNext}
        isNextDisabled={!selectedTemplate}
        nextButtonText="Continue to Overview"
        backButtonText="Back to Compare & Select"
      />
    </div>
  );
};

export default TemplateSelectionStep;
